import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import HeartIcon from "components/icons/Heart";
import HeartFilled from "components/icons/HeartFilled";
import Button from "components/components/atoms/Button";
import breakpoints from "utils/breakpoints";
import useBreakpoint from "utils/hooks/useBreakpoint";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "tailwind.config.js";
import cx from "classnames";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import quickStartLogo from "assets/quickStartLogo.svg";
import OpenNewTab from "components/icons/OpenNewTab";
import Calculator from "components/icons/Calculator";
import MortgageCalculatorModal from "components/components/organisms/MortgageCalculatorModal";
import Info from "components/icons/Info";
import LazyLoad from "react-lazyload";
import { getPriceDifferential } from "utils/getPriceDifferential";

const fullConfig = resolveConfig(tailwindConfig);
const CardContainer = styled.div`
  margin-top: 0.313rem;
  margin-bottom: 0.25rem;
  @media ${breakpoints.md} {
    margin-bottom: 0.25rem;
  }
`;

const Card = styled.div`
  margin-top: 1rem;
  margin-bottom: 2rem;
  @media ${breakpoints.md} {
    margin-bottom: 1rem;
  }
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  min-height: 1.625rem;
  width: fit-content;

  @media ${breakpoints.md} {
    ${({ islogo }) =>
      islogo
        ? ``
        : `${Card}:hover & {
      color: white;
      background-color: ${fullConfig.theme.colors["mattamy-blue-light"]};
    }`}
  }

  ${({ isMobile, islogo }) =>
    islogo ? `` : isMobile ? `min-width: 7.5rem;` : `min-width: 8rem;`}
`;

const QMIFlagMessageContainer = styled.div`
  border-top-right-radius: 0.813rem;
  border-bottom-right-radius: 0.813rem;
  border-top-width: 0.125rem;
  border-bottom-width: 0.125rem;
  border-right-width: 0.125rem;
`;

const QMIFlagMessage = styled.div`
  min-height: 1.625rem;
  width: fit-content;

  ${({ isMobile }) => (isMobile ? `min-width: 7.5rem;` : `min-width: 8rem;`)}
`;

const CardTitle = styled.h3`
  letter-spacing: -0.029rem;
`;
const CardPrice = styled.span`
  letter-spacing: -0.029rem;
`;

const CardSubtitle = styled.div`
  letter-spacing: 0.007rem;
`;

const CardDescription = styled.div`
  letter-spacing: 0.007rem;
`;

const Img = styled.img`
  height: 13.75rem;
  transition: transform 0.5s ease;

  @media ${breakpoints.md} {
    min-height: 15rem;

    ${Card}:hover & {
      transform: scale(1.15);
    }
  }
`;

const ImageContainer = styled.div`
  @media ${breakpoints.md} {
    max-width: 22.5rem;
  }
`;

const ProductInfo = styled.div`
  @media ${breakpoints.lg} {
    min-height: 15rem;
  }
`;

const IconContainer = styled.div`
  width: 2.188rem;
  height: 2.188rem;
`;

const StyledButton = styled(Button)`
  height: 3.125rem;
`;

const Tooltip = styled.div`
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  top: 150%;
  width: 80%;
  left: 15%;
  @media ${breakpoints.md} {
    width: 100%;
    left: 1%;
  }
`;

const TooltipLogo = styled.div`
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  min-width: 30%;
  @media ${breakpoints.md} {
    max-width: 50%;
  }
`;
const QuickStartLogoFlag = styled.div`
  border-radius: 0.813rem;
  min-height: 1.625rem;
  width: fit-content;
`;

const SearchCard = (props) => {
  const {
    image,
    type,
    copy,
    city,
    community,
    state,
    price,
    title,
    attributes,
    subtitle,
    availableDateMessage,
    statusShortDescription = "",
    modelHome = "",
    url,
    id,
    favoritesList = [],
    saveFavorite,
    deleteFavorite,
    ctaAction,
    tooltipText,
    homeType,
    planName,
    isCondo = false,
    selectedProduct,
    communityStyles,
    caContentText,
    usContentText,
    country,
    //displayParkingSpace = true,
    preSellingHome = false,
    quickStartHomesTooltip,
    countryQMI,
    den = false,
    mortgageAmountsModal = {},
    activeQMIs = false,
    messageFlag,
    rvGarage,
    priceDifferentialText,
  } = props;
  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs"].includes(breakpoint);
  const isTablet = ["md"].includes(breakpoint);
  const isSelected = selectedProduct && id ? id === selectedProduct : false;
  const mortgageCalculatorTooltip =
    mortgageAmountsModal && mortgageAmountsModal.tooltip;
  const [
    showModalMortgageCalculator,
    setShowModalMortgageCalculator,
  ] = useState(false);
  const [
    showModalMortgageCalculatorTooltip,
    setShowModalMortgageCalculatorTooltip,
  ] = useState(false);
  const [readCardData, SetReadCardData] = useState(false);
  const [showQuickStartTooltip, setShowQuickStartTooltip] = useState(false);

  const statusShortDescriptionCommunity =
    statusShortDescription && statusShortDescription.length > 50
      ? statusShortDescription.substring(0, 47) + "..."
      : statusShortDescription;

  // Click outside functionality
  const showModalMortgageCalculatorModal =
    mortgageAmountsModal && mortgageAmountsModal.displayMortgageModal;
  const showPreviousPrice = price && price.displayPreviousPrice;
  const mortgageCalculatorTooltipRef = useRef();
  const quickStartTooltipRef = useRef();

  useOnClickOutside(mortgageCalculatorTooltipRef, () => {
    setTimeout(() => {
      if (showModalMortgageCalculatorTooltip)
        setShowModalMortgageCalculatorTooltip(false);
    }, 100);
  });

  useOnClickOutside(quickStartTooltipRef, () => {
    setTimeout(() => {
      if (isMobile && showQuickStartTooltip) setShowQuickStartTooltip(false);
    }, 100);
  });

  const hasMobileSrc = type === "Community" || type === "MPC";

  const imageSrc =
    (isMobile && hasMobileSrc
      ? image && image.srcMobile
      : image && image.srcDesktop) ||
    (image && image.src);
  const isFavorite = favoritesList.includes(id);

  const isZeroValue = (label) => {
    if (label) {
      const labelNumber = label.split(" ")[0];
      if (labelNumber && labelNumber === "0") return true;
      return false;
    }
    return true;
  };
  const getLabel = (label, hasDen = false) => {
    const labelValues = label.split(" ");
    let returnlabel = "";
    if (labelValues && labelValues.length > 0) {
      if (labelValues[1] === "-") {
        returnlabel = labelValues[2] > 1 ? label + "s" : label;
      }
      returnlabel = labelValues[0] > 1 ? label + "s" : label;
    }
    if (returnlabel !== "")
      return hasDen ? returnlabel + " + Den" : returnlabel;
    return label;
  };
  const getMpcHomeTypes = (homeTypes) => {
    if (homeTypes) {
      let homeTypesList = homeTypes.split(", ");
      homeTypesList = homeTypesList.filter((e) => e);
      return [].map
        .call(homeTypesList, function (e) {
          return e;
        })
        .join(", ");
    }
    return "";
  };
  const getAtributesFirstLinePlan = (attributes) => {
    const beds = attributes.find((e) => e.icon === "bed") || {};
    const baths = attributes.find((e) => e.icon === "bath") || {};
    const halfBaths = attributes.find((e) => e.icon === "half-baths") || {};
    const bedsValues = beds.label.split(" ");
    const isStudio = isCondo && bedsValues[0] === "0" && bedsValues[1] !== "-";
    return (
      <div>
        {isStudio ? (
          <span>{"Studio"}</span>
        ) : (
          <span>
            {!isZeroValue(beds.label) &&
              getLabel(beds.label, isCondo && den && type === "Plan")}
          </span>
        )}
        {baths && !isZeroValue(baths.label) && (
          <>
            <span className="px-2">|</span>
            <span>{getLabel(baths.label)}</span>
          </>
        )}
        {halfBaths && !isZeroValue(halfBaths.label) && (
          <>
            <span className="px-2">|</span>
            <span>{getLabel(halfBaths.label)}</span>
          </>
        )}
      </div>
    );
  };
  const getAtributesSecondLinePlan = (attributes) => {
    const sqft = attributes.find((e) => e.icon === "ruler") || {};
    const garage = attributes.find((e) => e.icon === "car") || {};
    const garageValues = garage.label.split(" ");
    const isZeroGarageValue =
      garageValues[0] === "0" && garageValues[1] !== "-";
    return (
      <div>
        <span>{!isZeroValue(sqft.label) && sqft.label}</span>
        {isZeroGarageValue || isZeroValue(sqft.label) ? (
          ""
        ) : (
          <span className="px-2">|</span>
        )}
        {isZeroGarageValue ? (
          ""
        ) : rvGarage ? (
          <span>{garage.label[0] + " Car + RV Garage"}</span>
        ) : (
          <span>{garage.label}</span>
        )}
      </div>
    );
  };
  const getAtributesFirstLineQMI = (attributes) => {
    const beds = attributes.find((e) => e.icon === "bed") || {};
    const baths = attributes.find((e) => e.icon === "bath") || {};
    const halfBaths = attributes.find((e) => e.icon === "half-baths") || {};
    const bedsValues = beds.label && beds.label.split(" ");
    const isStudio = isCondo && bedsValues[0] === "0";
    return (
      <div>
        {isStudio ? (
          <span>{"Studio"}</span>
        ) : (
          <span>{!isZeroValue(beds.label) && getLabel(beds.label)}</span>
        )}
        {baths && !isZeroValue(baths.label) && (
          <>
            <span className="px-2">|</span>
            <span>{getLabel(baths.label)}</span>
          </>
        )}
        {halfBaths && !isZeroValue(halfBaths.label) && (
          <>
            <span className="px-2">|</span>
            <span>{getLabel(halfBaths.label)}</span>
          </>
        )}
      </div>
    );
  };
  const getAtributesSecondLineQMI = (attributes) => {
    const garage = attributes.find((e) => e.icon === "car") || {};
    const sqft = attributes.find((e) => e.icon === "ruler") || {};
    return (
      <div>
        {sqft && !isZeroValue(sqft.label) && (
          <>
            <span>{sqft.label}</span>
          </>
        )}
        {garage && !isZeroValue(garage.label) && (
          <>
            <span className="px-2">|</span>
            {rvGarage ? (
              <span>{garage.label[0] + " Car + RV Garage"}</span>
            ) : (
              <span>{garage.label}</span>
            )}
          </>
        )}
      </div>
    );
  };
  const getDefaultContentTextValue = (attributes) => {
    for (let i = 0; i < attributes.length; i++) {
      if (!isZeroValue(attributes[i].label) && attributes[i].icon !== "stories")
        return false;
    }
    return true;
  };
  const getCommunityStylesFormat = (communityStyles) => {
    const styles = communityStyles.split(",");
    if (styles && styles.length > 1) {
      let newFormat = styles.reduce(function (a, b) {
        return a.concat(", " + b);
      });
      return newFormat;
    }

    return communityStyles;
  };

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };

  return (
    <>
      <CardContainer
        className={cx("w-full", {
          "p-1 border-4 border-mattamy-blue-light rounded": isSelected,
        })}
        id={`${id}`}
      >
        <Card
          className="bg-mattamy-white flex flex-col lg:flex-row justify-right items-start md:cursor-pointer"
          onKeyUp={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.keyCode === 13) {
              window.location.href = url;
            }
          }}
          isSelected={isSelected}
        >
          <ImageContainer
            isMobile={isMobile}
            className="w-full h-full text-center relative rounded overflow-hidden"
          >
            <div className="overflow-hidden rounded">
              <LazyLoad
                width={360}
                overflow={true}
                once={true}
                scrollContainer={"#SearchResultList"}
              >
                <Img
                  aria-hidden="true"
                  src={imageSrc}
                  alt={(image && image.alt) || ""}
                  className={"bg-gray-300 object-cover w-full"}
                  onMouseEnter={(e) => (e.target.ariaHidden = false)} // Disable href on hover
                  onMouseLeave={(e) => (e.target.ariaHidden = true)}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    window.location.href = url;
                  }}
                />
              </LazyLoad>

              {messageFlag && (
                <QMIFlagMessageContainer className="absolute top-0 left-0 mt-3 items-center justify-center rounded-r text-promo-dark-blue bg-aqua border-y-2 border-promo-dark-blue">
                  <QMIFlagMessage
                    isMobile={isMobile}
                    className="flex items-center justify-center mt-1 md:mt-0 px-2"
                  >
                    <p className="px-1 text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 md:mt-1">
                      {messageFlag}
                    </p>
                  </QMIFlagMessage>
                </QMIFlagMessageContainer>
              )}

              <IconContainer
                aria-hidden="true"
                className="absolute top-0 right-0 mt-3 mr-3 items-center justify-center rounded-full
           text-mattamy-blue-light bg-white hover:bg-mattamy-blue hover:text-white
           hover:ring-2 hover:ring-mattamy-blue-light"
                onClick={(e) => {
                  e.nativeEvent.stopPropagation();
                  e.stopPropagation();
                  handleFavoriteClick(id);
                }}
                // Enable href on blur
                onMouseEnter={(e) => {
                  e.target.ariaHidden = false;
                }}
                onMouseLeave={(e) => {
                  e.target.ariaHidden = true;
                }}
                onBlur={(e) => {
                  e.stopPropagation();
                  e.target.ariaHidden = true;
                }}
                onFocus={(e) => {
                  e.stopPropagation();
                  e.target.ariaHidden = false;
                }}
                role="button"
                aria-label={
                  isFavorite ? "Mark as unfavorite" : "Mark as favorite"
                }
                aria-pressed={isFavorite ? "true" : "false"}
                tabIndex={0}
              >
                {isFavorite ? (
                  <HeartFilled className="h-5 w-5 mt-2 ml-2" />
                ) : (
                  <HeartIcon className="h-5 w-5 mt-2 ml-2" />
                )}
              </IconContainer>
            </div>
            {ctaAction && ctaAction.text && ctaAction.href && (
              <button
                tabIndex={-1}
                aria-hidden="true"
                className="hidden lg:flex w-full h-10 mt-10-mattamy flex items-center justify-center bg-action-blue text-white
                font-trade-gothic-20 rounded pt-1"
                onMouseEnter={(e) => (e.target.ariaHidden = false)} // Disable href on hover
                onMouseLeave={(e) => (e.target.ariaHidden = true)}
                target={ctaAction.target}
                onKeyUp={(e) => {
                  e.nativeEvent.stopPropagation();
                  e.stopPropagation();
                  e.preventDefault();
                }}
                onClick={() => {
                  //console.log({ ctaAction });
                  if (ctaAction.target === "_blank")
                    window.open(ctaAction.href, "_blank").focus();
                  else window.location.href = ctaAction.href;
                  if (
                    ctaAction.text.toLowerCase() ===
                    "Join interest list".toLowerCase()
                  )
                    window.dataLayer.push({ event: "join-list" });
                  if (
                    ctaAction.text.toLowerCase() ===
                    "Register for Updates".toLowerCase()
                  )
                    window.dataLayer.push({ event: "register" });
                }}
                aria-label={
                  ctaAction.text +
                  ". " +
                  (ctaAction &&
                  ctaAction.target &&
                  ctaAction.target === "_blank"
                    ? "(Opens in new tab)"
                    : "")
                }
              >
                {ctaAction.text}
                {ctaAction &&
                  ctaAction.target &&
                  ctaAction.target === "_blank" && (
                    <OpenNewTab className="-mt-1 ml-1 w-5" />
                  )}
              </button>
            )}
          </ImageContainer>
          <ProductInfo
            id="ProductInfo"
            className="pt-2 lg:pt-0 w-full lg:w-1/2 flex flex-col justify-between"
          >
            <div tabIndex="-1" className="block lg:pl-5">
              <div className="flex flex-row">
                {((type === "Community" && statusShortDescription.length > 0) ||
                  (type === "Plan" && modelHome.length > 0)) && (
                  <a tabIndex={-1} href={url}>
                    <AvailableFlag
                      isMobile={isMobile}
                      islogo={false}
                      className="flex items-center justify-center bg-white border-mattamy-blue-light border-2 border-mattamy-blue-light hover:md:bg-mattamy-blue-light
                mt-1 md:mt-0 hover:md:text-white text-mattamy-blue-light px-2"
                    >
                      <p className="px-1 text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1">
                        {type === "Community"
                          ? statusShortDescriptionCommunity
                          : ""}
                        {type === "Plan" ? modelHome : ""}
                      </p>
                      <img
                        style={{
                          position: "absolute",
                          width: "1px",
                          height: "1px",
                          padding: "0",
                          margin: "-1px",
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          border: "0",
                        }}
                        src={image.src || ""}
                        alt={image.alt || ""}
                      />
                      <button
                        style={{
                          position: "absolute",
                          width: "1px",
                          height: "1px",
                          padding: "0",
                          margin: "-1px",
                          overflow: "hidden",
                          clip: "rect(0, 0, 0, 0)",
                          border: "0",
                        }}
                        tabIndex={-1}
                        aria-label={
                          ctaAction.text +
                          ". " +
                          (ctaAction &&
                          ctaAction.target &&
                          ctaAction.target === "_blank"
                            ? "(Opens in new tab)"
                            : "")
                        }
                      />
                    </AvailableFlag>
                  </a>
                )}
                {type === "QMI" &&
                  (preSellingHome && countryQMI === "United States" ? (
                    <QuickStartLogoFlag
                      className="flex items-center justify-center bg-white border-mattamy-blue-light border-2 border-mattamy-blue-light 
mt-1 md:mt-0 hover:md:text-white text-mattamy-blue-light px-2 h-6 ml-2"
                      onClick={() => {
                        if (isMobile) setShowQuickStartTooltip(true);
                      }}
                      onMouseEnter={() => setShowQuickStartTooltip(true)}
                      onMouseLeave={() => setShowQuickStartTooltip(false)}
                    >
                      <img
                        className="object-cover h-full rounded"
                        src={quickStartLogo}
                        alt="quickStartLogo..."
                      />
                    </QuickStartLogoFlag>
                  ) : (
                    availableDateMessage.length > 0 && (
                      <a tabIndex={-1} className="flex flex-col" href={url}>
                        <AvailableFlag
                          isMobile={isMobile}
                          islogo={false}
                          className="flex items-center justify-center bg-white border-mattamy-blue-light border-2 border-mattamy-blue-light hover:md:bg-mattamy-blue-light
mt-1 md:mt-0 hover:md:text-white text-mattamy-blue-light px-2"
                        >
                          <p className="px-1 text-center font-trade-gothic-20 uppercase text-sm tracking-wide leading-3 mt-1">
                            {availableDateMessage}
                          </p>
                        </AvailableFlag>
                      </a>
                    )
                  ))}

                {quickStartHomesTooltip && showQuickStartTooltip && (
                  <TooltipLogo
                    ref={quickStartTooltipRef}
                    className="absolute mt-8 px-6 py-4 bg-white rounded z-30 text-left text-action-blue text-sm cursor-default"
                  >
                    {quickStartHomesTooltip}
                  </TooltipLogo>
                )}
              </div>

              <a tabIndex={-1} href={url}>
                <CardSubtitle
                  className={cx(
                    "font-graphie text-mattamy-blue md:text-mattamy-blue-light text-sm md:text-base leading-6 tracking-widest",
                    {
                      "mt-1 md:mt-0": type === "MPC",
                      "mt-2 md:mt-3": type !== "MPC",
                    }
                  )}
                >
                  {type === "Community"
                    ? (city ? city + ", " : "") + state
                    : ""}
                  {type === "Plan" || (type === "QMI" && isMobile)
                    ? community
                    : ""}
                  {type === "QMI" && !isMobile ? city + ", " + community : ""}
                  {type === "MPC" ? getMpcHomeTypes(homeType || subtitle) : ""}
                </CardSubtitle>
                <CardTitle className="font-trade-gothic-20 text-mattamy-blue text-3xl leading-8 md:mt-1">
                  {title}
                </CardTitle>
                {type === "Plan" || type === "QMI" ? (
                  <CardDescription className="font-graphie text-mattamy-blue md:text-mattamy-blue-light text-base leading-relaxed md:leading-5 mb-1 md:mb-0">
                    {type === "QMI"
                      ? planName
                        ? `${planName} Floorplan | ${homeType}`
                        : homeType
                      : homeType}
                  </CardDescription>
                ) : (
                  ""
                )}
                <CardDescription
                  className="font-graphie text-mattamy-blue md:text-mattamy-blue-light text-base leading-relaxed md:leading-5 mb-5 md:mb-0"
                  type={type}
                >
                  {type === "Community" && (
                    <p className="text-mattamy-gray-font text-lg font-trade-gothic-20 text-left pb-3 font-bold">
                      {communityStyles
                        ? getCommunityStylesFormat(communityStyles)
                        : ""}
                    </p>
                  )}
                  {type === "Community" || type === "MPC" ? copy : ""}
                  {type === "Plan" && (
                    <>
                      {getDefaultContentTextValue(attributes) ? (
                        country === "USA" ? (
                          usContentText
                        ) : (
                          caContentText
                        )
                      ) : (
                        <>
                          <p className="mb-1 md:mb-0">
                            {getAtributesFirstLinePlan(attributes)}
                          </p>
                          <p>{getAtributesSecondLinePlan(attributes)}</p>
                        </>
                      )}
                    </>
                  )}
                  {type === "QMI" && (
                    <>
                      <p className="mb-1 md:mb-0">
                        {getAtributesFirstLineQMI(attributes)}
                      </p>
                      <p>{getAtributesSecondLineQMI(attributes)}</p>
                    </>
                  )}
                </CardDescription>
              </a>
            </div>

            {type === "Plan" && activeQMIs && (
              <button
                className={cx(
                  "font-graphie border-1-5 border-action-blue bg-action-blue text-white rounded w-48 flex justify-center text-md h-8",
                  {
                    "my-2": !isMobile && modelHome.length > 0,
                    "ml-4": !isMobile,
                    "mb-3 pt-1": isMobile,
                  }
                )}
                onClick={() => {
                  window.open(url + "#availablehomes", "_blank").focus();
                  //window.location.href = url + "#availablehomes";
                }}
              >
                View Quick Move-Ins
                <OpenNewTab className="mt-1 ml-1 w-5" />
              </button>
            )}

            <div className="flex items-baseline flex-col">
              {price.price && price.price !== "$0" ? (
                <a
                  tabIndex={-1}
                  href={url}
                  className="block lg:pl-5 flex items-baseline md:flex-col"
                >
                  <CardSubtitle className="font-graphie text-mattamy-blue-light text-sm md:text-base leading-7">
                    {price.normalText}
                  </CardSubtitle>
                  <CardPrice className="flex font-trade-gothic-20 text-mattamy-blue text-3xl leading-9 md:-mb-1 ml-3 md:ml-0">
                    <span>{price.price} </span>
                    <span className="ml-4 line-through text-mattamy-gray-light">
                      {showPreviousPrice && price.previousPriceQMI}
                    </span>
                    {showPreviousPrice && (
                      <p className="ml-4 -mt-2 text-mattamy-gray-light flex flex-col font-graphie leading-5">
                        <span className="text-xs leading-2">
                          {priceDifferentialText}{" "}
                        </span>
                        <span className="text-lg leading-5 font-trade-gothic-20">
                          {getPriceDifferential(
                            price.previousPriceQMI,
                            price.price
                          )}
                        </span>
                      </p>
                    )}
                  </CardPrice>
                </a>
              ) : (
                //(type === "Plan" || type === "QMI") && (
                <a
                  tabIndex="-1"
                  href={url}
                  className="block lg:pl-5 flex items-baseline md:flex-col"
                >
                  <CardPrice className="font-trade-gothic-20 text-mattamy-blue text-3xl leading-9 md:-mb-1 ml-0">
                    {price.noPriceText}
                  </CardPrice>
                </a>
                //)
              )}
              {(type === "Plan" || type === "QMI") &&
                price.pricePerMonth &&
                price.pricePerMonth !== "$0" &&
                showModalMortgageCalculatorModal && (
                  <div className="relative lg:pl-5 mt-2 w-full">
                    <CardSubtitle
                      className={cx(
                        "flex font-graphie text-mattamy-blue-light text-sm md:text-base leading-7 -mt-1 hover:underline font-bold"
                      )}
                    >
                      <button
                        className={cx(
                          "flex font-graphie text-mattamy-blue-light text-sm md:text-base leading-7 -mt-1 hover:underline font-bold"
                        )}
                        onClick={(e) => {
                          e.nativeEvent.stopPropagation();
                          if (!showModalMortgageCalculator) {
                            if (typeof window !== "undefined") {
                              window.dataLayer.push({
                                event: "MortgageCalcLinkClickS",
                              });
                              window.gtag("event", "MortgageCalcLinkClick");
                            }
                            setShowModalMortgageCalculator(true);
                          }
                        }}
                      >
                        <Calculator className={"h-6"} />
                        {mortgageAmountsModal &&
                          mortgageAmountsModal.cardDisplayText + " "}
                        {mortgageAmountsModal &&
                          mortgageAmountsModal.cardDisplayPrice &&
                          price.pricePerMonth}
                        {/*"Estimate Monthly Payment "*/}
                        <svg
                          className={cx("w-6 mt-1 mr-2 transform rotate-180")}
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            fill="currentColor"
                            d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                          />
                        </svg>
                      </button>
                      {mortgageCalculatorTooltip &&
                        mortgageCalculatorTooltip.length > 0 && (
                          <button
                            onClick={(e) => {
                              setShowModalMortgageCalculatorTooltip(
                                !showModalMortgageCalculatorTooltip
                              );
                            }}
                          >
                            <Info className={" h-4 transform"} />
                          </button>
                        )}
                    </CardSubtitle>
                    {showModalMortgageCalculatorTooltip &&
                      mortgageCalculatorTooltip && (
                        <Tooltip
                          ref={mortgageCalculatorTooltipRef}
                          className="z-10 absolute px-6 py-4 bg-white rounded text-left text-action-blue text-sm cursor-default"
                          //data-aos="fade-down"
                          //data-aos-duration="400"
                          //data-aos-delay="50"
                        >
                          {mortgageCalculatorTooltip}
                        </Tooltip>
                      )}
                  </div>
                )}
            </div>

            {(isTablet || isMobile) && (
              <>
                <StyledButton
                  color={"action-blue"}
                  variant="filled"
                  className="block text-base mt-1 pt-0 pb-0 md:w-full"
                  href={url}
                  aria-label={"view " + title}
                >
                  {`view ${title}`}
                </StyledButton>
                {ctaAction && ctaAction.text && ctaAction.href && (
                  <StyledButton
                    color={"action-blue"}
                    variant="filled"
                    className="block text-base mt-4 pt-0 pb-0 md:w-full"
                    href={ctaAction.href}
                    target={ctaAction.target}
                    onClick={() => {
                      if (
                        ctaAction.text.toLowerCase() ===
                        "Join interest list".toLowerCase()
                      )
                        window.dataLayer.push({ event: "join-list" });
                      if (
                        ctaAction.text.toLowerCase() ===
                        "Register for Updates".toLowerCase()
                      )
                        window.dataLayer.push({ event: "register" });
                    }}
                    aria-label={
                      ctaAction.text +
                      ". " +
                      (ctaAction &&
                      ctaAction.target &&
                      ctaAction.target === "_blank"
                        ? "(Opens in new tab)"
                        : "")
                    }
                  >
                    {ctaAction.text}
                  </StyledButton>
                )}
              </>
            )}
          </ProductInfo>
        </Card>
      </CardContainer>
      <MortgageCalculatorModal
        isOpen={showModalMortgageCalculator}
        onClose={() => {
          setShowModalMortgageCalculator(false);
        }}
        disclaimer={mortgageAmountsModal && mortgageAmountsModal.disclaimerText}
        ctaText={mortgageAmountsModal && mortgageAmountsModal.ctaText}
        ctaTitle={mortgageAmountsModal && mortgageAmountsModal.ctaTitle}
        ctaURL={
          mortgageAmountsModal &&
          mortgageAmountsModal.ctaurl &&
          mortgageAmountsModal.ctaurl.href
        }
        amountValue={
          price.price && price.price.replace("$", "").replace(/,/g, "")
        }
        downpaymentInit={
          mortgageAmountsModal && mortgageAmountsModal.paymentPercentage
        }
        interestRateInit={
          mortgageAmountsModal && mortgageAmountsModal.interestRate
        }
        loanTermInit={mortgageAmountsModal && mortgageAmountsModal.termInYears}
        title={mortgageAmountsModal && mortgageAmountsModal.mortgageTitle}
      />
    </>
  );
};

SearchCard.propTypes = {
  props: PropTypes.object,
};

export default SearchCard;
